import React from 'react';
import dayjs from 'dayjs';
import cx from 'classnames';
import { Field, FormSpy, withTypes } from 'react-final-form';
import { isDesktop, notDesktop } from '@zola-helpers/client';
import { Toggle, ButtonV2, Link } from '@zola/zola-ui';
import { browserHistory, withRouter, Redirect } from 'react-router';

import {
  ConfigureAddressCollectionLinkFormStateProps,
  ConfigureAddressCollectionLinkFormDispatchProps,
} from 'components/manage/guestlist/addressCollection/ConfigureAddressCollectionLinkForm';

import {
  FormInput,
  FormDatePicker,
  Condition,
  composeValidators,
} from 'components/common/form/FinalFormV2Elements';
import ConfigureAddressCollectionHeader from 'components/manage/guestlist/addressCollection/ConfigureAddressCollectionHeader';
import ConfigureAddressCollectionPhotoUploader from 'components/manage/guestlist/addressCollection/ConfigureAddressCollectionPhotoUploader';

import ConfigureAddressCollectionFormValues from 'components/manage/guestlist/addressCollection/@types/formValues';

import * as Validations from 'components/common/form/commonValidations';

import styles from './ConfigureAddressCollectionLinkForm.module.less';

const validator = composeValidators(
  Validations.REQUIRED,
  Validations.DATE,
  Validations.CHECK_CURRENT_WEDDING_DATE,
  Validations.DATE_WITHIN_VALID_RANGE
);

type ConfigureAddressCollectionLinkFormProps = ConfigureAddressCollectionLinkFormStateProps &
  ConfigureAddressCollectionLinkFormDispatchProps & {
    currentStep?: number;
    className?: string;
    setCurrentStep?: (step: number) => void;
    onFormSpy: (values: ConfigureAddressCollectionFormValues) => void;
  };

const ConfigureAddressCollectionLinkForm: React.FC<ConfigureAddressCollectionLinkFormProps> = ({
  initialValues,
  currentStep,
  setCurrentStep,
  onCreateOrUpdateAddressCollection,
  onFormSpy,
  tempImg,
  savedImg,
  savedImgUuid,
  isUploadCareBusy,
  ucWeddingDate,
  className,
}) => {
  const hasImg = tempImg || savedImg;

  const onFormSave = (
    values: ConfigureAddressCollectionFormValues
  ): Promise<typeof Redirect | void> => {
    const { weddingDate, ...rest } = values;
    const formattedFormDateValue = dayjs(weddingDate).format('YYYY-MM-DD');
    const newWeddingDate =
      values.showWeddingDate && ucWeddingDate !== formattedFormDateValue && formattedFormDateValue;
    const finalImageUuid = hasImg ? tempImg?.uuid || savedImgUuid : '';
    const mergedValues = {
      ...rest,
      imageUuid: finalImageUuid,
      ...(newWeddingDate && { weddingDate: newWeddingDate }),
    };
    return onCreateOrUpdateAddressCollection(mergedValues).then((): void =>
      browserHistory.push('/wedding/manage/guests/collection-link')
    );
  };

  const onSetCurrentStep = (step: number): void | null => {
    if (setCurrentStep) return setCurrentStep(step);
    return null;
  };

  const renderStep1Ctas = (): JSX.Element | null => {
    if (isDesktop() || currentStep === 2) return null;
    if (hasImg) {
      return (
        <ButtonV2 fullWidth onClick={(): void | null => onSetCurrentStep(2)}>
          Next: Add A Message
        </ButtonV2>
      );
    }
    if (isUploadCareBusy) return null;
    return (
      <Link
        role="button"
        onClick={(): void | null => onSetCurrentStep(2)}
        arrow
        size="small"
        className={styles.skipLink}
      >
        Skip Photo
      </Link>
    );
  };

  const renderBackButton = (): JSX.Element | null => {
    if (notDesktop() && currentStep === 2) {
      return (
        <ButtonV2
          variant="secondary"
          className={styles.secondaryButton}
          onClick={(): void | null => onSetCurrentStep(1)}
        >
          Back
        </ButtonV2>
      );
    }
    return null;
  };

  const renderSubmitButton = (isValid: boolean, handleSubmit: () => void): JSX.Element | null => {
    if (currentStep === 1) return null;
    return (
      <ButtonV2
        className={styles.primaryButton}
        type="submit"
        disabled={!isValid}
        onClick={handleSubmit}
      >
        See My Magic Link
      </ButtonV2>
    );
  };

  const { Form } = withTypes<ConfigureAddressCollectionFormValues>();

  return (
    <div className={cx(styles.formContainer, className)}>
      <Form initialValues={initialValues} onSubmit={onFormSave}>
        {({ valid, handleSubmit }: { valid: boolean; handleSubmit: () => void }): JSX.Element => (
          <>
            {isDesktop() || currentStep === 1 ? (
              <ConfigureAddressCollectionHeader />
            ) : (
              <h1 className={styles.title}>Add a Message</h1>
            )}
            {(isDesktop() || currentStep === 1) && <ConfigureAddressCollectionPhotoUploader />}
            {(isDesktop() || currentStep === 2) && (
              <>
                <FormInput
                  name="linkTitle"
                  label="Your Message"
                  placeholder="Can you tap and share your address? 😉 Xo"
                  maxChars={40}
                  validate={[Validations.MAX_LENGTH_40]}
                />
                <div className={styles.toggleSection}>
                  <div className={styles.toggleInput}>
                    <label htmlFor="showWeddingDate" className={styles.toggleLabel}>
                      Show Wedding Date
                    </label>
                    <Field name="showWeddingDate" initialValue>
                      {(props): JSX.Element => (
                        <Toggle
                          withIcons
                          inline
                          id="showWeddingDate"
                          ariaLabel="Show Wedding Date to all guests"
                          {...props}
                        />
                      )}
                    </Field>
                    <Condition when="showWeddingDate" is>
                      <FormDatePicker
                        name="weddingDate"
                        id="weddingDate"
                        customValidator={validator}
                        calendarIcon
                        isRequired
                        className={styles.datePicker}
                      />
                    </Condition>
                  </div>
                </div>
              </>
            )}
            <div className={styles.buttons}>
              {renderStep1Ctas()}
              {renderBackButton()}
              {renderSubmitButton(valid, handleSubmit)}
            </div>
            <FormSpy
              subscription={{ values: true }}
              onChange={(props): void => onFormSpy(props.values)}
            />
          </>
        )}
      </Form>
    </div>
  );
};

export default withRouter(ConfigureAddressCollectionLinkForm);



// WEBPACK FOOTER //
// ./src/components/manage/guestlist/addressCollection/ConfigureAddressCollectionLinkForm/ConfigureAddressCollectionLinkForm.tsx